import PBService from "@irati/utils/services"
import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import {
  QueryGetTopUpArgs,
  MutationCompleteTopUpArgs,
  QueryGetTopUpsArgs,
  MutationCreateTopUpArgs,
  CreateTenantWalletTransfersInput,
  MutationUpdateServiceFeesTiersArgs,
  MutationDeleteServiceFeesTiersArgs,
  MutationWaiveServiceFeesByTenantArgs,
  QueryTopupCurrencyCodeArgs,
  QueryTopupTaxForBusinessArgs
} from "@irati/types/ops"
import {QueryServiceFeesTiersByTenantArgs, TiersResponse, Topup, Tax} from "@irati/types"
import topupCurrencyCode, {topupCurrencyCodeKey} from "./queries/getCurrencyCode"
import getTopUpQuery, {getTopUpKey} from "./queries/getTopUp"
import completeTopupMutation, {completeTopupKey} from "./mutations/completeTopup"
import getTopUpsQuery, {getTopUpsKey} from "./queries/getTopUps"
import createTopUpMutation, {createTopUpKey} from "./mutations/createTopup"
import createTenantWalletTransfers from "./mutations/TenantWalletTransfers"
import serviceFeesTiersQuery, {serviceFeesTiersKey} from "./queries/serviceFeesTiers"
import waiveServiceFeesByTenant from "./mutations/waiveServiceFeesByTenant"
import serviceFeesTiersByTenantQuery, {serviceFeesByTenantTiersKey} from "./queries/serviceFeesTiersByTenant"
import updateServiceFeesTiersMutation from "./mutations/updateServiceFeesTiers"
import deleteServiceFeesTiersMutation from "./mutations/deleteServiceFeesTiers"
import getTaxForTenant, {topupTaxForBusinessKey} from "./queries/getTaxForTenant"
import topupErrorHandlers from "./errors/topupErrorHandler"

class TopUpWallet extends PBService {
  list(args: QueryGetTopUpsArgs, options: QueryOptions = {}) {
    const variables = {
      ...args
    }
    return client.query({...options, query: getTopUpsQuery, variables}).then(({data}) => data[getTopUpsKey])
  }

  get(topupId: string, options: QueryOptions = {}): Promise<Topup> {
    const variables: QueryGetTopUpArgs = {topupId}
    return client.query({...options, query: getTopUpQuery, variables}).then(({data}) => data[getTopUpKey])
  }

  create(args: MutationCreateTopUpArgs, options: MutationOptions = {}): Promise<Topup> {
    const variables = {
      ...args
    }
    return client.mutate({...options, mutation: createTopUpMutation, variables}).then(({data}) => data[createTopUpKey])
  }

  complete(topupId: string, options: MutationOptions = {}) {
    const variables: MutationCompleteTopUpArgs = {topupId}
    return client.mutate({...options, mutation: completeTopupMutation, variables}).then(({data}) => data[completeTopupKey])
  }

  createLegacyTenantWalletTransfer(data: CreateTenantWalletTransfersInput): Promise<boolean> {
    return client.mutate({mutation: createTenantWalletTransfers, variables: {data}}).then(() => true)
  }

  serviceFeesTiers(options: QueryOptions = {}): Promise<TiersResponse[]> {
    return client.query({...options, query: serviceFeesTiersQuery}).then(({data}) => data[serviceFeesTiersKey])
  }

  serviceFeesTiersByTenant(variables: QueryServiceFeesTiersByTenantArgs, options: QueryOptions = {}): Promise<TiersResponse[]> {
    return client.query({...options, query: serviceFeesTiersByTenantQuery, variables}).then(({data}) => data[serviceFeesByTenantTiersKey])
  }

  async serviceFeesWaived(): Promise<boolean> {
    const serviceFeesTiers = await this.serviceFeesTiers()
    if (!serviceFeesTiers?.length) {
      return true
    }
    return serviceFeesTiers.every((tier) => tier.tierServiceFee === 0)
  }

  async serviceFeesWaivedByTenant(variables: QueryServiceFeesTiersByTenantArgs): Promise<boolean> {
    const serviceFeesTiers = await this.serviceFeesTiersByTenant(variables)
    if (!serviceFeesTiers?.length) {
      return true
    }
    return serviceFeesTiers.every((tier) => tier.tierServiceFee === 0)
  }

  saveServiceFeesTiers(args: MutationUpdateServiceFeesTiersArgs, options: MutationOptions = {}): Promise<boolean> {
    const variables = {
      ...args
    }
    return client.mutate({...options, mutation: updateServiceFeesTiersMutation, variables}).then(() => true)
  }

  deleteServiceFeesTiers(args: MutationDeleteServiceFeesTiersArgs, options: MutationOptions = {}): Promise<boolean> {
    const variables = {
      ...args
    }
    return client.mutate({...options, mutation: deleteServiceFeesTiersMutation, variables}).then(() => true)
  }

  waiveServiceFees(args: MutationWaiveServiceFeesByTenantArgs, options: MutationOptions = {}): Promise<boolean> {
    const variables = {
      ...args
    }
    return client.mutate({...options, mutation: waiveServiceFeesByTenant, variables}).then(() => true)
  }

  async getCurrencyCode({businessId = null}: QueryTopupCurrencyCodeArgs = {}, options: QueryOptions = {}): Promise<string> {
    const variables = {businessId}
    return client.query({...options, query: topupCurrencyCode, variables}).then(({data}) => data[topupCurrencyCodeKey])
  }

  async getTaxForTenant(args: QueryTopupTaxForBusinessArgs = {}, options: QueryOptions = {}): Promise<Tax> {
    return client.query({...options, query: getTaxForTenant, variables: args}).then(({data}) => data[topupTaxForBusinessKey])
  }
}

const topupService = new TopUpWallet({errorHandlers: [topupErrorHandlers]})

export default topupService
